/**
 * Adaptive quality: walks the existing PostFX preset ladder from frame-time
 * evidence so a weak GPU converges to a playable preset without the player
 * opening settings.
 *
 * Design constraints, in order:
 *  - never fight the player — one manual quality change in the menu disables it
 *  - never oscillate — a level we stepped down from twice is not climbed back into
 *  - never judge a warm-up — shader compiles right after boot and after every
 *    preset switch produce spikes that say nothing about steady-state cost
 */

const LADDER = ['potato', 'low', 'medium', 'high', 'ultra'];

const WINDOW_S = 1.75;      // evidence window
const FPS_DOWN = 45;        // below this → step down
const FPS_UP = 72;          // above this (twice in a row) → step up
const WARMUP_S = 6;         // ignore everything this long after boot / switch
const HITCH_S = 0.4;        // a frame this long is a stall, not a workload signal

export function createAutoQualitySystem() {
  let ctx = null;
  let enabled = false;
  let ceiling = LADDER.length - 1;
  let level = 0;
  let warmup = WARMUP_S;
  let winTime = 0, winFrames = 0, winSpoiled = false;
  let goodStreak = 0;
  const burned = new Array(LADDER.length).fill(0);
  let upCap = LADDER.length - 1;
  let toastEl = null, toastTimer = 0;

  function render() { return ctx?.render || ctx?.get?.('render') || null; }

  function setLevel(next, why) {
    next = Math.max(0, Math.min(next, Math.min(ceiling, upCap)));
    if (next === level) return;
    const preset = LADDER[next];
    level = next;
    warmup = 3; // the switch itself recompiles programs; do not judge it
    winTime = winFrames = 0; winSpoiled = false;
    goodStreak = 0;
    render()?.setQuality?.(preset);
    if (ctx) ctx.quality = preset;
    // Next visit boots straight into the settled preset instead of walking
    // down from 'high' again (each step down re-links every shader program).
    try { localStorage.setItem('blackout.aq.preset', preset); } catch { /* private mode */ }
    console.log(`[autoquality] ${why} → ${preset.toUpperCase()}`);
    toast(`画質を自動調整 → ${preset.toUpperCase()}`);
    ctx?.events?.emit('autoquality:change', { preset, why });
  }

  /** One evidence window's verdict. Exposed for the test harness. */
  function judge(fps) {
    if (!enabled) return;
    if (fps < FPS_DOWN) {
      goodStreak = 0;
      if (level > 0) {
        burned[level]++;
        // Stepped down from here twice: the level is beyond this machine.
        if (burned[level] >= 2) upCap = Math.min(upCap, level - 1);
        // Each preset step re-links every program (light counts are baked into
        // shaders), so a machine at half the target skips a level rather than
        // paying that hitch once per rung.
        const drop = fps < FPS_DOWN * 0.55 ? 2 : 1;
        setLevel(level - drop, `${Math.round(fps)} fps`);
      }
    } else if (fps > FPS_UP) {
      goodStreak++;
      if (goodStreak >= 2 && level < Math.min(ceiling, upCap)) {
        goodStreak = 0;
        setLevel(level + 1, `${Math.round(fps)} fps, headroom`);
      }
    } else {
      goodStreak = 0;
    }
  }

  function toast(text) {
    const root = document.getElementById('ui-root');
    if (!root) return;
    if (!toastEl) {
      toastEl = document.createElement('div');
      toastEl.className = 'aq-toast';
      root.appendChild(toastEl);
    }
    toastEl.textContent = text;
    toastEl.classList.add('show');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => toastEl.classList.remove('show'), 2600);
  }

  return {
    name: 'autoquality',
    priority: 9990,

    init(c) {
      ctx = c;
      enabled = !!c.config?.autoQuality;
      const start = LADDER.indexOf(c.quality);
      level = start >= 0 ? start : LADDER.length - 1;
      ceiling = LADDER.length - 1;
      // The player touching the quality row in settings is an instruction.
      c.events?.on('ui:setting', ({ id }) => {
        if (id === 'quality' && enabled) {
          enabled = false;
          console.log('[autoquality] manual quality set — auto disabled');
        }
      });
      c.autoquality = {
        get enabled() { return enabled; },
        get preset() { return LADDER[level]; },
        set enabled(v) { enabled = !!v; },
        judge, // synthetic windows for tests
      };
    },

    update(dt) {
      if (!enabled || document.hidden) return;
      if (warmup > 0) { warmup -= dt; return; }
      winTime += dt;
      winFrames++;
      if (dt > HITCH_S) winSpoiled = true;
      if (winTime < WINDOW_S) return;
      const fps = winFrames / winTime;
      const spoiled = winSpoiled;
      winTime = winFrames = 0; winSpoiled = false;
      if (!spoiled) judge(fps);
    },

    dispose() {
      clearTimeout(toastTimer);
      toastEl?.remove();
    },
  };
}
